import { useEffect, useState, useCallback, useRef, lazy, Suspense } from 'react';
import { isTuningEligible } from '@playcastdotio/PeerCore/tuning';
import { motion } from 'framer-motion';
import { Player } from './Player';
import { QuickstartTheme, type StreamBounds, getResolvedCtaUrl, getResolvedFullscreenAnimationUrl, getResolvedJoinIdleAnimationUrl, getResolvedFullscreenBackgroundImageUrl } from '../../features/quickstart/types/theme';
import { DesktopControlsAttractOverlay } from './quickstart/DesktopControlsAttractOverlay';
import { MobileAttractOverlay } from './quickstart/MobileAttractOverlay';
import { MobileFullscreenAnimationOverlay } from './quickstart/MobileFullscreenAnimationOverlay';
import { MobileJoinIdleAnimationOverlay } from './quickstart/MobileJoinIdleAnimationOverlay';
import { QuickstartPlayerDebugOverlay } from './quickstart/QuickstartPlayerDebugOverlay';
import { GuestDebugPanel, type GuestDebugSection } from './debug/GuestDebugPanel';
import { StreamDebugSection } from './debug/StreamDebugSection';
import { isDebugBuild } from '@playcastdotio/PeerCore';
import { AnimatedWishlistButton } from './quickstart/AnimatedWishlistButton';
import { ReconnectingOverlay } from './quickstart/ReconnectingOverlay';
import { AfkOverlay } from './quickstart/AfkOverlay';
import { MobileGamepadInputStandalone, MobileGamepadUiStandalone, MobileGamepadInputEvent, gamepadStore, RotateToLandscape } from '@playcastdotio/MobileGamepad';
import { useShouldShowMobileGamepad } from '@playcastdotio/SharedComponents';
import { gamepadState, mediaStreamState, playerConnectionState } from '../../api/stores/playerStores';
import { setGamepadState } from '../../api/modules/hostMessaging/inputEvents';
import type { QuickstartController } from '../../features/quickstart/QuickstartController';
import type { PlaycastMessage, PlaycastMessageTarget } from '@playcastdotio/Messaging';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { SignalQualityIndicator } from '../../features/quickstart/components/SignalQualityIndicator';
import { signalBarsState } from '../../api/stores/signalBarsStore';
import { useIsLandscape, useIsPortraitOrientation, useScreenLockOrientation } from '../../features/quickstart/utils/responsiveHooks';
import { reportOrientationMismatch } from '../../features/quickstart/utils/orientationDiagnostics';
import {
  isIosSafariVideoGestureRequired,
  shouldAutoDismissMobileAttractOverlay,
} from './iosVideoPlaybackPolicy';

import config from '../../config.json';
import constants from '@playcastdotio/GlobalConfig';
import { captureStore } from '@playcastdotio/InputCapture';
import { Z_PLAYER_HUD, Z_GAMEPAD } from '../../features/quickstart/config/zIndexLayers';
const StreamTuningPanel = lazy(() => import('../../features/stream-tuning/components/StreamTuningPanel').then(module => ({ default: module.StreamTuningPanel })));

interface PlayerWithQuickstartProps {
  quickstartMode: boolean;
  /** True during preparing_player phase — player is mounted but behind loading overlay */
  preparing?: boolean;
  afkTimerActive: boolean;
  adTimerActive: boolean;
  afkTimeoutSeconds: number;
  adTimerSeconds: number;
  shouldShowPlayNowOverlay?: boolean;
  theme: QuickstartTheme;
  access?: {
    mouse?: boolean;
    keyboard?: boolean;
    touch?: boolean;
    gamepad?: boolean;
  };
  onFirstInput: () => void;
  requestBrowserLocks?: () => Promise<void>;
  onWishlistClicked: () => void;
  onAfkTimeout: () => void;
  onAdTimerExpired: () => void;
  onDisconnect: () => void;
  isReconnecting?: boolean;
  connectionRetries?: number;
  isDebugMode?: boolean;
  debugSections?: GuestDebugSection[];
  controller?: QuickstartController | null;
  getAFKRemainingTime: () => number;
  getAdRemainingTime: () => number;
  // In-game idle (AFK overlay)
  inGameIdleActive?: boolean;
  inGameIdleTimeoutSeconds?: number;
  resetInGameIdleTimer?: () => void;
  getInGameIdleRemainingTime?: () => number;
  forceInGameIdle?: () => void;
}

/**
 * Player wrapped with Quickstart-specific UI and behavior
 */
export const PlayerWithQuickstart = ({
  quickstartMode,
  preparing = false,
  afkTimerActive,
  adTimerActive,
  afkTimeoutSeconds,
  adTimerSeconds,
  shouldShowPlayNowOverlay: propsShowPlayNowOverlay,
  theme,
  access: propsAccess,
  onFirstInput,
  requestBrowserLocks,
  onWishlistClicked,
  onAfkTimeout,
  onAdTimerExpired,
  onDisconnect,
  isReconnecting = false,
  connectionRetries: _connectionRetries = 0,
  isDebugMode = false,
  debugSections = [],
  controller = null,
  getAFKRemainingTime,
  getAdRemainingTime,
  inGameIdleActive = false,
  inGameIdleTimeoutSeconds = 0,
  resetInGameIdleTimer,
  getInGameIdleRemainingTime,
  forceInGameIdle,
}: PlayerWithQuickstartProps) => {
  const [streamBounds, setStreamBounds] = useState<StreamBounds>({ width: 0, height: 0, top: 0, left: 0 });

  const [showPlayNowOverlay, setShowPlayNowOverlay] = useState(!isDebugMode);
  const [signalQuality, setSignalQuality] = useState(playerConnectionState.currentState.signalQuality);
  const [signalBarsEnabled, setSignalBarsEnabled] = useState(signalBarsState.currentState.enabled);
  // Single latched reconnect cover (5A) — coordinator + stall, held across media clears.
  const [reconnectCoverLatched, setReconnectCoverLatched] = useState(false);

  const handleSignalBarsToggle = useCallback(() => {
    setSignalBarsEnabled(!signalBarsState.currentState.enabled);
  }, []);

  useEffect(() => {
    const id = playerConnectionState.addListener((state) => {
      setSignalQuality(state.signalQuality);
    });
    return () => playerConnectionState.removeListener(id);
  }, []);

  // Mobile attract overlay - blur over video with pulsing gamepad buttons
  const [showAttractOverlay, setShowAttractOverlay] = useState(false);
  const attractActiveRef = useRef(false);

  // Hint button: highlight a single face button until the user presses any button
  const [hintActive, setHintActive] = useState(true);

  // Track last fullscreen attempt timestamp for throttling retries
  const lastFullscreenAttemptRef = useRef(0);

  // Timer remaining times - pulled from backend timer manager (synced)
  // These are purely for UI display, not for triggering callbacks
  const [afkRemainingTime, setAfkRemainingTime] = useState(afkTimeoutSeconds);
  const [playtimeRemainingTime, setPlaytimeRemainingTime] = useState(adTimerSeconds);
  const [idleRemainingTime, setIdleRemainingTime] = useState(inGameIdleTimeoutSeconds);

  // Mobile gamepad state
  const [gamepadSlot, setGamepadSlot] = useState<number>(-1);
  const [isPhysicalGamepadConnected, setIsPhysicalGamepadConnected] = useState(false);

  // Touch-based mobile detection - detects touch as primary input
  // This handles iPads in desktop mode that still need touch UI
  const isMobileDevice = useShouldShowMobileGamepad();

  // Detect rotation for wishlist button positioning
  const isLandscape = useIsLandscape();
  const isPortraitOrientation = useIsPortraitOrientation();
  const shouldRotate = isLandscape && isPortraitOrientation;

  // Ask the OS to lock orientation while in fullscreen. On Android Chrome / iOS PWA
  // this prevents the page from rotating at all, eliminating the orientation race
  // entirely. On iOS Safari (regular tab) the API rejects and we fall back to the
  // CSS @media (orientation: portrait) rotation defined in styles.css.
  useScreenLockOrientation('landscape');

  // One-shot diagnostic: if the three orientation signals (matchMedia,
  // screen.orientation, and innerWidth-vs-Height) ever disagree at the moment
  // a force-rotation is being applied, log + dispatch a custom event so we can
  // verify in the wild that the new CSS-driven path is holding.
  useEffect(() => {
    if (shouldRotate) reportOrientationMismatch('PlayerWithQuickstart.shouldRotate');
  }, [shouldRotate]);

  // CTA corner position (hoisted so both machine ID and wishlist button can reference it)
  type CtaCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  const ctaRotationMap: Record<CtaCorner, CtaCorner> = {
    'top-left': 'top-right',
    'top-right': 'bottom-right',
    'bottom-right': 'bottom-left',
    'bottom-left': 'top-left',
  };
  const baseCtaPosition: CtaCorner = (isMobileDevice
    ? theme.wishlistButtonPosition?.mobile
    : theme.wishlistButtonPosition?.desktop) as CtaCorner
    || (isMobileDevice ? 'bottom-right' : 'top-right');
  const effectiveCtaPosition = shouldRotate ? ctaRotationMap[baseCtaPosition] : baseCtaPosition;
  const machineIdPosition = (getResolvedCtaUrl(theme) && effectiveCtaPosition === 'top-left')
    ? 'top-right' as const : 'top-left' as const;

  // Get access rights from props (passed from controller state)
  const access = propsAccess ?? {
    mouse: false,
    keyboard: false,
    touch: false,
    gamepad: false,
  };

  // Debug mode states
  const [debugShowMobileGamepad, setDebugShowMobileGamepad] = useState(false);
  const [debugHideAttractOverlay, setDebugHideAttractOverlay] = useState(true);

  // Delay gamepad rendering until layout hooks (orientation, rotation) have settled.
  // We ALSO re-arm this gate on orientationchange / matchMedia changes so the
  // gamepad has a chance to remount with consistent orientation state across all
  // its readers. Cheap and defensive — costs one render gap per rotation.
  const [gamepadReady, setGamepadReady] = useState(false);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const arm = () => {
      setGamepadReady(false);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setGamepadReady(true), 150);
    };

    arm();

    const portraitMq = window.matchMedia('(orientation: portrait)');
    const onChange = () => arm();
    if (typeof portraitMq.addEventListener === 'function') {
      portraitMq.addEventListener('change', onChange);
    } else {
      (portraitMq as unknown as { addListener: (cb: () => void) => void }).addListener(onChange);
    }
    window.addEventListener('orientationchange', onChange);

    return () => {
      if (timer) clearTimeout(timer);
      if (typeof portraitMq.removeEventListener === 'function') {
        portraitMq.removeEventListener('change', onChange);
      } else {
        (portraitMq as unknown as { removeListener: (cb: () => void) => void }).removeListener(onChange);
      }
      window.removeEventListener('orientationchange', onChange);
    };
  }, []);

  // Track fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);

  // On iOS Safari, the Fullscreen API is not supported — document.fullscreenElement
  // is never set. We use a higher-level "entered stream" flag that works on both:
  // - Android: set when fullscreen is actually entered
  // - iOS: set on first touch (since that's the iOS equivalent of "entry")
  // This flag is one-way: once the user enters, the fullscreen animation never returns.
  const [hasEnteredStream, setHasEnteredStream] = useState(false);
  const fullscreenApiAvailable = typeof document !== 'undefined' &&
    (document.fullscreenEnabled || !!(document as Document & { webkitFullscreenEnabled?: boolean }).webkitFullscreenEnabled);

  const resolvedFullscreenAnimationUrl = getResolvedFullscreenAnimationUrl(theme);
  const resolvedJoinIdleAnimationUrl = getResolvedJoinIdleAnimationUrl(theme);
  const resolvedFullscreenBackgroundImageUrl = getResolvedFullscreenBackgroundImageUrl(theme);

  const showFullscreenAnimation =
    isMobileDevice &&
    !!(theme.mobileFullscreenAnimation?.enabled) &&
    !!resolvedFullscreenAnimationUrl &&
    !hasEnteredStream &&
    !showPlayNowOverlay;

  const showFullscreenAnimationRef = useRef(showFullscreenAnimation);
  showFullscreenAnimationRef.current = showFullscreenAnimation;

  // Join/idle animation state machine
  type JoinIdleAnimState = 'hidden' | 'playing_hold' | 'playing_dismissable' | 'playing_idle';
  const [joinIdleAnimState, setJoinIdleAnimState] = useState<JoinIdleAnimState>('hidden');
  const joinIdleAnimStateRef = useRef<JoinIdleAnimState>('hidden');
  joinIdleAnimStateRef.current = joinIdleAnimState;
  const inputReceivedDuringHold = useRef(false);
  const hasTriggeredJoinAnimation = useRef(false);

  // Handle mobile gamepad input and forward to peer
  const handleMobileGamepadInput = useCallback((event: MobileGamepadInputEvent) => {
    if (event.gamepadState) {
      setGamepadState(event.gamepadState);
    }

    // Request fullscreen if not already active, throttled to once per 2s to allow retries
    // after silent failures (e.g. chain-delay buttons fire from setTimeout with no user gesture)
    if (!document.fullscreenElement) {
      const now = Date.now();
      if (now - lastFullscreenAttemptRef.current > 2000) {
        lastFullscreenAttemptRef.current = now;
        requestBrowserLocks?.();
      }
      // On iOS (no fullscreen API), gamepad input is the "entry" signal
      if (!fullscreenApiAvailable) {
        setHasEnteredStream(true);
      }
    }

    if (attractActiveRef.current) {
      attractActiveRef.current = false;
      setShowAttractOverlay(false);
    }

    if (hintActive) {
      setHintActive(false);
    }

    if (joinIdleAnimState === 'playing_hold') {
      inputReceivedDuringHold.current = true;
    } else if (joinIdleAnimState === 'playing_dismissable' || joinIdleAnimState === 'playing_idle') {
      setJoinIdleAnimState('hidden');
    }

    resetInGameIdleTimer?.();
  }, [hintActive, joinIdleAnimState, fullscreenApiAvailable, requestBrowserLocks, resetInGameIdleTimer]);

  const handleAttractFirstInteraction = useCallback(() => {
    console.log('[PlayerWithQuickstart] Attract overlay tap - requesting browser locks + unmuting audio');

    if (attractActiveRef.current) {
      attractActiveRef.current = false;
      setShowAttractOverlay(false);
    }

    requestBrowserLocks?.();

    const videoElement = document.querySelector('video:not([data-overlay-video])') as HTMLVideoElement | null;
    if (videoElement) {
      videoElement.muted = false;
      videoElement.play().catch((err) => {
        console.warn('[PlayerWithQuickstart] Audio unmute failed:', err);
      });
    }
  }, [requestBrowserLocks]);

  // Handle debug overlay mount - register message and controller input callbacks
  const handleDebugOverlayMount = useCallback(
    (
      messageCallback: (message: PlaycastMessage<PlaycastMessageTarget>) => void,
      controllerInputCallback: (inputType: string, eventType: string) => void,
    ) => {
      if (controller) {
        controller.setDebugMessageCallback(messageCallback);
        controller.setDebugControllerInputCallback(controllerInputCallback);
      }
    },
    [controller],
  );

  // Check for physical gamepad
  useEffect(() => {
    const checkGamepad = () => {
      const gamepads = navigator.getGamepads?.() || [];
      const hasPhysical = Array.from(gamepads).some((gp) => gp !== null);
      setIsPhysicalGamepadConnected(hasPhysical);
    };

    checkGamepad();
    window.addEventListener('gamepadconnected', checkGamepad);
    window.addEventListener('gamepaddisconnected', checkGamepad);

    return () => {
      window.removeEventListener('gamepadconnected', checkGamepad);
      window.removeEventListener('gamepaddisconnected', checkGamepad);
    };
  }, []);

  // Fullscreen/pointer lock/visibility handlers are now managed by QuickstartController
  // See setupBrowserLockHandlers() in QuickstartController.ts

  // Track fullscreen state for UI changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      if (fs) setHasEnteredStream(true);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    // Check initial state
    const initialFs = !!document.fullscreenElement;
    setIsFullscreen(initialFs);
    if (initialFs) setHasEnteredStream(true);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // On Android (fullscreen API available), reset the entry gate when exiting fullscreen
  // so the fullscreen animation can reappear. On iOS this never fires since
  // hasEnteredStream is one-way (fullscreen API doesn't exist).
  useEffect(() => {
    if (fullscreenApiAvailable && !isFullscreen) {
      setHasEnteredStream(false);
      hasTriggeredJoinAnimation.current = false;
      setJoinIdleAnimState('hidden');
    }
  }, [isFullscreen, fullscreenApiAvailable]);

  // Join/idle animation — start trigger: stream entered on mobile
  useEffect(() => {
    if (
      hasEnteredStream &&
      isMobileDevice &&
      theme.mobileJoinIdleAnimation?.enabled &&
      resolvedJoinIdleAnimationUrl &&
      joinIdleAnimState === 'hidden' &&
      !hasTriggeredJoinAnimation.current
    ) {
      hasTriggeredJoinAnimation.current = true;
      setJoinIdleAnimState('playing_hold');
    }
  }, [hasEnteredStream, isMobileDevice, theme.mobileJoinIdleAnimation?.enabled, resolvedJoinIdleAnimationUrl, joinIdleAnimState]);

  // Join/idle animation — hold timer logic
  useEffect(() => {
    if (joinIdleAnimState !== 'playing_hold') return;
    inputReceivedDuringHold.current = false;

    const timer = setTimeout(() => {
      if (inputReceivedDuringHold.current) {
        setJoinIdleAnimState('hidden');
      } else {
        setJoinIdleAnimState('playing_dismissable');
      }
    }, (theme.mobileJoinIdleAnimation?.holdSeconds ?? 5) * 1000);

    return () => clearTimeout(timer);
  }, [joinIdleAnimState]);

  // Join/idle animation — idle re-trigger (only fires on idle state transitions)
  useEffect(() => {
    if (
      inGameIdleActive &&
      theme.mobileJoinIdleAnimation?.enabled &&
      theme.mobileJoinIdleAnimation?.showDuringIdle &&
      joinIdleAnimStateRef.current === 'hidden'
    ) {
      setJoinIdleAnimState('playing_idle');
    } else if (!inGameIdleActive && joinIdleAnimStateRef.current === 'playing_idle') {
      setJoinIdleAnimState('hidden');
    }
  }, [inGameIdleActive]);

  // Show mobile gamepad controls when the user has gamepad access (or debug mode)
  // Gated on gamepadReady to prevent rendering before orientation/rotation hooks settle
  const shouldShowMobileGamepad = gamepadReady && ((isMobileDevice && access.gamepad && (!showPlayNowOverlay || showAttractOverlay)) || (isDebugMode && debugShowMobileGamepad));

  // Show the mobile menu/exit strip for any mobile user in active play state — regardless of
  // whether they have gamepad access. This ensures keyboard/mouse-only mobile users can always
  // exit the stream (fixes: no exit UI when gamepad access is denied).
  const shouldShowMobileMenu = gamepadReady && ((isMobileDevice && (!showPlayNowOverlay || showAttractOverlay)) || (isDebugMode && debugShowMobileGamepad));

  // Any mobile player who is actively in the stream (overlay dismissed) can use a
  // touch to enter fullscreen — independent of gamepad access. Touch-only campaigns
  // turn gamepad access off, so gating the touch→fullscreen listener on
  // `shouldShowMobileGamepad` meant a touch-native game never went fullscreen
  // (issue #13833).
  const shouldEnableTouchFullscreen = isMobileDevice && (!showPlayNowOverlay || showAttractOverlay);

  // [GamepadVisibility] Diagnostic (#13862): logs every gate feeding shouldShowMobileGamepad.
  // `hostGrantReceived` separates the three failure shapes: no grant arrived at all, a grant
  // arrived that denied gamepad (host clamped it — check the campaign's supportedInputs), or a
  // grant allowed gamepad but a render gate is hiding it. Gated to non-production so it fires in
  // an on-device repro (local/dev/staging) but never ships to prod.
  useEffect(() => {
    if (constants.ENVIRONMENT === 'production') return;
    console.log('[GamepadVisibility]', {
      shouldShowMobileGamepad,
      accessGamepad: access.gamepad,
      hostGrantReceived: captureStore.hasHostAccess(),
      isMobileDevice,
      gamepadReady,
      showPlayNowOverlay,
      showAttractOverlay,
      isDebugMode,
      fullAccess: access,
    });
  }, [shouldShowMobileGamepad, access, isMobileDevice, gamepadReady, showPlayNowOverlay, showAttractOverlay, isDebugMode]);

  // Dedicated touchstart listener — handles both fullscreen entry and animation dismissal.
  // On Android: first touch requests fullscreen; subsequent touches (in fullscreen) dismiss animations.
  // On iOS: first touch marks stream as "entered" (no fullscreen API); subsequent touches dismiss animations.
  useEffect(() => {
    if (!shouldEnableTouchFullscreen) return;

    const handleFirstTouch = () => {
      // If the fullscreen animation overlay is showing, it handles its own dismissal
      // via onDismiss — don't let the window listener also fire.
      if (showFullscreenAnimationRef.current) return;

      const isInFullscreen = !!document.fullscreenElement;

      if (!isInFullscreen && fullscreenApiAvailable) {
        // Android: request fullscreen (fullscreenchange handler will set hasEnteredStream)
        lastFullscreenAttemptRef.current = Date.now();
        requestBrowserLocks?.();
      } else if (!fullscreenApiAvailable && !hasEnteredStream) {
        // iOS: no fullscreen API — first touch is the "entry" event
        setHasEnteredStream(true);
        requestBrowserLocks?.();
      } else {
        // Already in stream — handle join/idle animation dismissal
        if (joinIdleAnimStateRef.current === 'playing_hold') {
          inputReceivedDuringHold.current = true;
        } else if (joinIdleAnimStateRef.current === 'playing_dismissable' || joinIdleAnimStateRef.current === 'playing_idle') {
          setJoinIdleAnimState('hidden');
        }
      }
    };

    window.addEventListener('touchstart', handleFirstTouch, { capture: true });
    return () => window.removeEventListener('touchstart', handleFirstTouch, { capture: true });
  }, [shouldEnableTouchFullscreen, hasEnteredStream, fullscreenApiAvailable, requestBrowserLocks]);

  // Sync Play Now overlay visibility with coordinator's shouldShowPlayNowOverlay state
  // This allows the coordinator to explicitly control when the overlay is shown or hidden
  useEffect(() => {
    if (propsShowPlayNowOverlay !== undefined) {
      setShowPlayNowOverlay(propsShowPlayNowOverlay);
    }
  }, [propsShowPlayNowOverlay]);

  // Listen to gamepad state for slot assignment
  useEffect(() => {
    // TODO: Get actual player ID from auth
    const playerId = 'quickstart-player';

    const listenerId = gamepadState.addListener((state) => {
      if (state.playerMappings[playerId]?.gamepadIndexes.length > 0) {
        setGamepadSlot(state.playerMappings[playerId].gamepadIndexes[0]);
      } else {
        setGamepadSlot(-1);
      }
    });

    return () => {
      gamepadState.removeListener(listenerId);
    };
  }, []);

  // Reset timer display when props change
  useEffect(() => {
    setAfkRemainingTime(afkTimeoutSeconds);
  }, [afkTimeoutSeconds]);

  useEffect(() => {
    setPlaytimeRemainingTime(adTimerSeconds);
  }, [adTimerSeconds]);

  // AFK Timer countdown - poll from backend timer manager (synchronized)
  // UI ONLY - does not trigger callbacks, purely for display
  useEffect(() => {
    if (!showPlayNowOverlay) return; // Only update when overlay is visible

    // Poll timer manager every 100ms for smooth countdown
    const interval = setInterval(() => {
      const remaining = getAFKRemainingTime();
      setAfkRemainingTime(remaining);
    }, 100);

    return () => clearInterval(interval);
  }, [showPlayNowOverlay, getAFKRemainingTime]);

  // Playtime Timer countdown - poll from backend timer manager (synchronized)
  // UI ONLY - does not trigger callbacks, purely for display
  useEffect(() => {
    if (!adTimerActive) return;
    if (showPlayNowOverlay) return; // Pause when overlay is shown

    // Poll timer manager every 100ms for smooth countdown
    const interval = setInterval(() => {
      const remaining = getAdRemainingTime();
      setPlaytimeRemainingTime(remaining);
    }, 100);

    return () => clearInterval(interval);
  }, [adTimerActive, showPlayNowOverlay, getAdRemainingTime]);

  // In-Game Idle timer countdown - poll when idle overlay is active
  useEffect(() => {
    if (!inGameIdleActive || !getInGameIdleRemainingTime) return;

    const interval = setInterval(() => {
      setIdleRemainingTime(getInGameIdleRemainingTime());
    }, 100);

    return () => clearInterval(interval);
  }, [inGameIdleActive, getInGameIdleRemainingTime]);

  // Listen for disconnect
  useEffect(() => {
    const handleDisconnect = () => {
      console.log('[PlayerWithQuickstart] Peer disconnected');
      onDisconnect();
    };

    window.addEventListener('playcast:peer:disconnected', handleDisconnect);

    return () => {
      window.removeEventListener('playcast:peer:disconnected', handleDisconnect);
    };
  }, [onDisconnect]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayNowStart = useCallback(() => {
    console.log('[PlayerWithQuickstart] Play Now button clicked - starting playtime');
    setShowPlayNowOverlay(false);

    // Re-establish the transport FIRST when one is pending. After a non-deliberate
    // disconnect the coordinator re-shows this overlay, and until this call existed
    // activating it only moved phases — the peer was gone, so the button did nothing at all
    // and the user sat on a frozen frame. A no-op on the happy path, and after a deliberate
    // end the coordinator has no pending rejoin so nothing is resurrected.
    void controller?.rejoinAfterConnectionLoss();

    // Notify controller that Play Now was clicked
    // This transitions from playing_afk -> playing_active and starts ad timer
    onFirstInput();
  }, [controller, onFirstInput]);

  // Desktop attract-mode dismissal. Mirrors PlayNowOverlay.handlePlayNow so the
  // first W/A/S/D keypress or mouse click drives the same gesture-gated flow:
  // pre-fullscreen video.play() (Android-safe; here purely defensive on desktop),
  // then await requestBrowserLocks() (fullscreen + pointer lock + keyboard lock),
  // then handlePlayNowStart() to flip the overlay off and trigger onFirstInput.
  // Must run synchronously from the keydown/mousedown handler so user activation
  // is still valid when the lock APIs are invoked.
  const handleDesktopFirstInput = useCallback(async () => {
    const videoElement = document.querySelector('video:not([data-overlay-video])') as HTMLVideoElement | null;
    if (videoElement) {
      videoElement.play().catch((err) => {
        console.warn('[PlayerWithQuickstart] Video play pre-fullscreen failed:', err);
      });
    }

    try {
      if (requestBrowserLocks) {
        await requestBrowserLocks();
      }
    } catch (error) {
      console.warn('[PlayerWithQuickstart] Browser locks not available, continuing anyway:', error);
    }

    handlePlayNowStart();
  }, [requestBrowserLocks, handlePlayNowStart]);

  // Auto-join on mobile: skip PlayNow overlay, go straight to gameplay.
  // Gated on `preparing` so this waits until the loading overlay has faded
  // (preparing_player → playing_afk transition).
  // NOTE: showPlayNowOverlay is included so this re-fires after phase bounces
  // (e.g., visibility change → playing_afk → coordinator sets shouldShowPlayNowOverlay: true).
  useEffect(() => {
    if (preparing) return;
    if (isMobileDevice && showPlayNowOverlay && quickstartMode) {
      console.log('[PlayerWithQuickstart] Mobile device detected - auto-joining');
      setShowPlayNowOverlay(false);
      const iosGestureRequired = isIosSafariVideoGestureRequired(
        typeof navigator !== 'undefined' ? navigator.userAgent : '',
        typeof document !== 'undefined' && 'ontouchend' in document,
      );
      if (shouldAutoDismissMobileAttractOverlay({ isMobileDevice, iosGestureRequired })) {
        setShowAttractOverlay(false);
        attractActiveRef.current = false;
      }
      onFirstInput();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileDevice, quickstartMode, preparing, showPlayNowOverlay]);

  const hintButtonMap: Record<string, string> = {
    A: 'South', B: 'East', X: 'West', Y: 'North',
    LB: 'LeftBumper', RB: 'RightBumper',
    LT: 'LeftTrigger', RT: 'RightTrigger',
    DpadUp: 'DpadUp', DpadDown: 'DpadDown', DpadLeft: 'DpadLeft', DpadRight: 'DpadRight',
    Start: 'Start', Select: 'Select',
  };
  const showHint = hintActive || inGameIdleActive;
  const configuredHint = theme.mobileGamepadConfig?.hintButton
    ? hintButtonMap[theme.mobileGamepadConfig.hintButton]
    : undefined;
  const hintDirection = showHint
    ? (configuredHint ?? (inGameIdleActive ? 'South' : undefined))
    : undefined;

  // Set gamepad store context with theme configuration for custom icons/layout
  useEffect(() => {
    const themeConfig = theme.mobileGamepadConfig;
    if (themeConfig && theme.slug) {
      const themeDefaults = {
        defaultLayout: themeConfig.defaultLayout,
        defaultHiddenButtons: themeConfig.defaultHiddenButtons,
        defaultOpacity: themeConfig.defaultOpacity,
        useCircularButtons: themeConfig.useCircularButtons,
        customButtonIcons: themeConfig.customButtonIcons,
        customButtonSizes: themeConfig.customButtonSizes,
      };

      gamepadStore.setContext(theme.slug, themeDefaults);
      console.log(`[PlayerWithQuickstart] Set gamepad context to: ${theme.slug}`, themeDefaults);
    }

    return () => {
      console.log(`[PlayerWithQuickstart] Clearing gamepad context`);
      gamepadStore.setContext('');
    };
  }, [theme.slug, theme.mobileGamepadConfig]);

  // Prevent drag-and-drop globally (similar to host-electron approach)
  useEffect(() => {
    const preventDrag = (e: DragEvent) => e.preventDefault();
    const preventContextMenu = (e: MouseEvent) => e.preventDefault();

    document.addEventListener('dragstart', preventDrag);
    document.addEventListener('contextmenu', preventContextMenu);

    return () => {
      document.removeEventListener('dragstart', preventDrag);
      document.removeEventListener('contextmenu', preventContextMenu);
    };
  }, []);

  return (
    <div
      className='relative h-screen w-screen select-none'
      style={
        {
          WebkitTouchCallout: 'none',
          touchAction: 'none',
        } as React.CSSProperties
      }
    >
      {/* Desktop Controls Attract Overlay - WASD + mouse + finger prompt that
          loops until the first W/A/S/D keypress or mouse click. Replaces the
          legacy "Click Here To Join" card (issue #13103). Mobile auto-joins
          with its own attract overlay further down. */}
      {quickstartMode && !isMobileDevice && (
        <DesktopControlsAttractOverlay
          visible={showPlayNowOverlay}
          onFirstInput={handleDesktopFirstInput}
        />
      )}

      {/* Mobile Attract Overlay - blur over video, dismissed on first gamepad input */}
      {isMobileDevice && (
        <MobileAttractOverlay
          visible={showAttractOverlay}
          backgroundOpacity={theme.mobileGamepadConfig?.attractModeConfig?.backgroundOpacity}
          onFirstInteraction={handleAttractFirstInteraction}
        />
      )}

      {/* Mobile Fullscreen Animation Overlay — always in DOM, visibility via prop */}
      {isMobileDevice && resolvedFullscreenAnimationUrl && (
        <MobileFullscreenAnimationOverlay
          animationUrl={resolvedFullscreenAnimationUrl}
          backgroundImageUrl={resolvedFullscreenBackgroundImageUrl}
          streamBounds={streamBounds}
          isForceRotated={shouldRotate}
          visible={showFullscreenAnimation}
          onDismiss={() => {
            setHasEnteredStream(true);
            const videoElement = document.querySelector('video:not([data-overlay-video])') as HTMLVideoElement | null;
            if (videoElement) {
              videoElement.muted = false;
              videoElement.play().catch(() => { /* iOS may reject autoplay */ });
            }
            requestBrowserLocks?.();
          }}
        />
      )}

      {/* Quickstart Overlays */}
      {quickstartMode && (
        <>
          {/* Play Time / AFK Remaining - Configurable Position (desktop only - mobile shows in gamepad Options button) */}
          {adTimerActive && !showPlayNowOverlay && !isMobileDevice && (() => {
            const position = theme.timerPosition || 'bottom';
            const positionClass = position === 'top' ? 'top-8' : 'bottom-8';
            const isAfk = inGameIdleActive;
            const displaySeconds = isAfk ? idleRemainingTime : playtimeRemainingTime;
            const totalSeconds = isAfk ? inGameIdleTimeoutSeconds : adTimerSeconds;

            return (
              <div className={`pointer-events-none fixed ${positionClass} left-1/2 -translate-x-1/2`} style={{ zIndex: Z_PLAYER_HUD }}>
                <div
                  className='relative overflow-hidden rounded-full shadow-lg'
                  style={{
                    width: isAfk ? '200px' : '140px',
                    animation: isAfk ? 'afk-desktop-glow 2.5s ease-in-out infinite' : undefined,
                  }}
                >
                  <div
                    className='absolute inset-y-0 right-0 transition-all duration-1000 ease-linear'
                    style={{
                      backgroundColor: isAfk ? '#FCA5A5' : '#4338CA',
                      width: `${Math.max(0, Math.min(100, totalSeconds > 0 ? (displaySeconds / totalSeconds) * 100 : 0))}%`,
                    }}
                  />

                  <div
                    className='absolute inset-0'
                    style={{ backgroundColor: isAfk ? 'rgba(127, 29, 29, 0.5)' : 'rgba(49, 46, 129, 0.5)' }}
                  />

                  <div className='relative flex items-center justify-center px-6 py-3'>
                    <span
                      className='text-2xl font-bold tabular-nums'
                      style={{ color: isAfk ? '#991B1B' : '#FFFFFF' }}
                    >
                      {isAfk ? `AFK | ${formatTime(displaySeconds)}` : formatTime(displaySeconds)}
                    </span>
                  </div>
                </div>
                {isAfk && (
                  <style>{`
                    @keyframes afk-desktop-glow {
                      0%, 100% { box-shadow: 0 0 8px 2px rgba(220, 38, 38, 0.4); }
                      50% { box-shadow: 0 0 18px 6px rgba(220, 38, 38, 0.8); }
                    }
                  `}</style>
                )}
              </div>
            );
          })()}

          {/* Wishlist Button (configurable position via theme) */}
          {/* Only show during gameplay - hide when Play Now overlay or attract overlay is visible */}
          {getResolvedCtaUrl(theme) && !showPlayNowOverlay && !showAttractOverlay && (() => {
            const position = effectiveCtaPosition;
            const basePosition = baseCtaPosition;

            // Check if we should use the animated button
            if (theme.useAnimatedWishlistButton) {
              const animatedText = theme.animatedWishlistButtonText || 'Wishlist\nNow';
              const animationSpeed = theme.animatedWishlistButtonSpeed || 1000;

              // When rotated, use base position for styling (border radius/padding)
              const stylePosition = shouldRotate ? basePosition : position;

              return (
                <AnimatedWishlistButton
                  position={position}
                  stylePosition={stylePosition}
                  isMobile={isMobileDevice}
                  onClick={onWishlistClicked}
                  text={animatedText}
                  animationDuration={animationSpeed}
                  shouldRotate={shouldRotate}
                />
              );
            }

            // Classic button (original implementation)
            // Position classes for each corner
            const positionClasses = {
              'top-left': 'left-0 top-0',
              'top-right': 'right-0 top-0',
              'bottom-left': 'left-0 bottom-0',
              'bottom-right': 'right-0 bottom-0',
            };

            // Border radius for each corner (rounded on the far corner)
            const borderRadius = {
              'top-left': '0 0 100px 0',      // rounded bottom-right
              'top-right': '0 0 0 100px',     // rounded bottom-left
              'bottom-left': '0 100px 0 0',   // rounded top-right
              'bottom-right': '100px 0 0 0',  // rounded top-left
            };

            // Padding adjustments for each corner
            const padding = {
              'top-left': isMobileDevice ? '4px 20px 8px 0' : '5px 55px 10px 0',
              'top-right': isMobileDevice ? '4px 0 8px 20px' : '5px 0 10px 55px',
              'bottom-left': isMobileDevice ? '8px 20px 4px 0' : '10px 55px 5px 0',
              'bottom-right': isMobileDevice ? '8px 0 4px 20px' : '10px 0 5px 55px',
            };

            // Button dimensions
            const buttonWidth = isMobileDevice ? 110 : 245;
            const buttonHeight = isMobileDevice ? 65 : 128;

            // When button is counter-rotated, we need to use the base position's styling
            // because the button will be rotated back to its original orientation
            const stylePosition = shouldRotate ? basePosition : position;

            // Get configurable CTA text (defaults to 'Wishlist!')
            const ctaText = theme.inGameCtaText || 'Wishlist!';

            return (
              <div
                className={`fixed ${positionClasses[position]} flex items-center justify-center`}
                style={{
                  zIndex: Z_PLAYER_HUD,
                  ...(shouldRotate ? {
                    width: `${buttonHeight}px`,
                    height: `${buttonWidth}px`,
                  } : undefined),
                }}
              >
              <button
                  onClick={onWishlistClicked}
                tabIndex={-1}
                className='pointer-events-auto flex flex-shrink-0 flex-col items-center justify-center border-[3px] border-black bg-gradient-to-b from-yellow-400 to-orange-500 shadow-2xl transition-opacity hover:opacity-90'
                style={{
                    borderRadius: borderRadius[stylePosition as Position],
                  width: `${buttonWidth}px`,
                  height: `${buttonHeight}px`,
                    padding: padding[stylePosition as Position],
                  gap: isMobileDevice ? '2px' : '5px',
                  touchAction: 'auto', // Override parent's touch-action: none to allow button clicks
                  // Counter-rotate the button when parent view is rotated
                  transform: shouldRotate ? 'rotate(90deg)' : undefined,
                  transformOrigin: 'center center',
                }}
              >
                <FontAwesomeIcon icon={faPlus} size={isMobileDevice ? 'lg' : '4x'} className='text-black' />
                <div className='text-left'>
                  <div className={`font-semibold leading-tight text-black ${isMobileDevice ? 'text-[8px]' : 'text-sm'}`}>
                    {isMobileDevice ? '' : isFullscreen ? 'Hold Esc' : 'Click'}
                  </div>
                  <div className={`font-semibold leading-tight text-black ${isMobileDevice ? 'text-[9px]' : 'text-sm'}`}>
                    {isMobileDevice ? `Tap to ${ctaText}` : `to ${ctaText}`}
                  </div>
                </div>
              </button>
            </div>
            );
          })()}
        </>
      )}

      {/* Debug Overlay */}
      {(isTuningEligible(isDebugBuild(), window.location.search) || (isDebugBuild() && isDebugMode)) && (
        <GuestDebugPanel accentColor={theme.primaryColor} sections={[
          ...(isDebugBuild() && isDebugMode ? [
          { id: 'stream', label: 'Stream', content: <StreamDebugSection /> },
          { id: 'player', label: 'Player', content: (
        <QuickstartPlayerDebugOverlay
          embedded
          theme={theme}
          sessionId={controller?.getSessionId()}
          mobileGamepadEnabled={debugShowMobileGamepad}
          onToggleMobileGamepad={() => setDebugShowMobileGamepad(!debugShowMobileGamepad)}
          attractOverlayHidden={debugHideAttractOverlay}
          onToggleAttractOverlay={() => setDebugHideAttractOverlay(!debugHideAttractOverlay)}
          onDebugMediaStream={(videoStream, audioStream) => {
            console.log('[PlayerWithQuickstart] Injecting debug media streams:', {
              hasVideo: !!videoStream,
              hasAudio: !!audioStream,
            });
            mediaStreamState.updateState({
              stream: videoStream || new MediaStream(),
              audioStream: audioStream || new MediaStream(),
            });
          }}
          onMount={handleDebugOverlayMount}
          onForceIdle={forceInGameIdle}
          inGameIdleActive={inGameIdleActive}
        />
          ) },
          ...debugSections,
          ] : []),
          ...(isTuningEligible(isDebugBuild(), window.location.search) ? [{ id: 'tuning', label: 'Tuning', content: <Suspense fallback={null}><StreamTuningPanel /></Suspense> }] : []),
        ]} />
      )}

      {/* Mobile Gamepad / Menu — always show the exit menu strip on mobile in active play state.
          Gamepad input and controls are only shown when the user has gamepad access.
          This ensures keyboard/mouse-only mobile users can always exit the stream. */}
      {/* Hide in portrait mode when force rotation is disabled */}
      {shouldShowMobileMenu && (config.FORCE_PORTRAIT_ORIENTATION || !isPortraitOrientation) && (
        <>
          {shouldShowMobileGamepad && (
            <MobileGamepadInputStandalone
              enabled={true}
              access={access}
              buttonChains={theme.mobileGamepadConfig?.buttonChains}
              onInput={handleMobileGamepadInput}
            />
          )}
          <motion.div
            className='fixed inset-0'
            style={{ zIndex: Z_GAMEPAD }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5, ease: 'easeOut' }}
          >
            <MobileGamepadUiStandalone
              enabled={true}
              testMode={isDebugMode && !debugHideAttractOverlay}
              gamepadSlot={gamepadSlot}
              isMouseEnabled={access.mouse}
              isKeyboardEnabled={access.keyboard}
              disconnect={onDisconnect}
              isPhysicalGamepadConnected={isPhysicalGamepadConnected}
              signalBarsToggle={handleSignalBarsToggle}
              signalBarsOn={signalBarsEnabled}
              hideGamepadControls={!shouldShowMobileGamepad}
              timerRemainingSeconds={
                inGameIdleActive
                  ? idleRemainingTime
                  : adTimerActive && !showPlayNowOverlay ? playtimeRemainingTime : undefined
              }
              timerTotalSeconds={
                inGameIdleActive
                  ? inGameIdleTimeoutSeconds
                  : adTimerActive && !showPlayNowOverlay ? adTimerSeconds : undefined
              }
              timerPosition={theme.timerPosition}
              afkMode={inGameIdleActive}
              defaultHiddenButtons={theme.mobileGamepadConfig?.defaultHiddenButtons}
              invisibleButtons={theme.mobileGamepadConfig?.invisibleButtons}
              defaultLayout={theme.mobileGamepadConfig?.defaultLayout}
              defaultOpacity={theme.mobileGamepadConfig?.defaultOpacity}
              useCircularButtons={theme.mobileGamepadConfig?.useCircularButtons}
              customButtonIcons={theme.mobileGamepadConfig?.customButtonIcons}
              customButtonSizes={theme.mobileGamepadConfig?.customButtonSizes}
              rightThumbstickBehindButtons={theme.mobileGamepadConfig?.rightThumbstickBehindButtons}
              isForceRotated={shouldRotate}
              attractHighlightedButton={hintDirection}
              attractOpacity={90}
              optionsDisabled={theme.disableOptionsButton}
            />
          </motion.div>
        </>
      )}

      {/* Mobile Join/Idle Animation Overlay — always in DOM, visibility via prop */}
      {isMobileDevice && resolvedJoinIdleAnimationUrl && theme.mobileJoinIdleAnimation?.enabled && (
        <MobileJoinIdleAnimationOverlay
          animationUrl={resolvedJoinIdleAnimationUrl}
          streamBounds={streamBounds}
          isForceRotated={shouldRotate}
          visible={joinIdleAnimState !== 'hidden' && !showFullscreenAnimation}
        />
      )}

      {/* AFK Overlay (in-game idle) */}
      <AfkOverlay
        visible={inGameIdleActive}
        remainingSeconds={idleRemainingTime}
        totalSeconds={inGameIdleTimeoutSeconds}
      />

      {/* Single latched reconnect cover (5A) — attempt counter intentionally hidden */}
      <ReconnectingOverlay visible={reconnectCoverLatched} />

      <SignalQualityIndicator level={signalQuality} connectionDown={reconnectCoverLatched} enabled={signalBarsEnabled} />

      {/* Rotate to Landscape Prompt - Shows when force rotation is disabled */}
      {!config.FORCE_PORTRAIT_ORIENTATION && <RotateToLandscape />}

      {/* Player Component — feeds stall into the single latched cover above */}
      <Player
        machineIdPosition={machineIdPosition}
        onStreamBoundsChange={setStreamBounds}
        reconnectingSignal={isReconnecting}
        onReconnectCoverLatchedChange={setReconnectCoverLatched}
        reconnectCoverAbandoned={showPlayNowOverlay && !isReconnecting}
      />
    </div>
  );
};

